package com.evoapps.evoshift.ui.growth

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.GroupAdd
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.domain.model.TeamJoin
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.viewmodels.RecommendPromptViewModel
import com.evoapps.evoshift.viewmodels.TeamInviteViewModel
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.recommend_prompt_action
import evoshift.composeapp.generated.resources.recommend_prompt_dismiss
import evoshift.composeapp.generated.resources.recommend_prompt_text
import evoshift.composeapp.generated.resources.recommend_prompt_title
import evoshift.composeapp.generated.resources.team_invite_prompt_action
import evoshift.composeapp.generated.resources.team_invite_prompt_dismiss
import evoshift.composeapp.generated.resources.team_invite_prompt_text
import evoshift.composeapp.generated.resources.team_invite_prompt_title
import org.jetbrains.compose.resources.stringResource

/** Test tags of the SH-239 dashboard cards. */
object GrowthCardTags {
    const val RECOMMEND_PROMPT = "recommend_prompt_card"
    const val TEAM_INVITE = "team_invite_prompt_card"
}

/** SH-239: once after joining a team, "Recommend EVoShift" on the dashboard; nothing otherwise. */
@Composable
fun RecommendPromptSection(viewModel: RecommendPromptViewModel, onRecommend: () -> Unit) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    // SH-247: decided after the dashboard: the card unfolds instead of popping in (and folds away).
    com.evoapps.evoshift.ui.theme.MotionVisibility(state.shown) {
        RecommendPromptCard(
            onRecommend = {
                viewModel.dismiss()
                onRecommend()
            },
            onDismiss = viewModel::dismiss
        )
    }
}

@Composable
fun RecommendPromptCard(onRecommend: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier) {
    PromptCard(
        icon = Icons.Default.Share,
        title = stringResource(Res.string.recommend_prompt_title),
        text = stringResource(Res.string.recommend_prompt_text),
        action = stringResource(Res.string.recommend_prompt_action),
        dismiss = stringResource(Res.string.recommend_prompt_dismiss),
        onAction = onRecommend,
        onDismiss = onDismiss,
        modifier = modifier.testTag(GrowthCardTags.RECOMMEND_PROMPT)
    )
}

/**
 * SH-239: the owner's "Invite the team to the app" after the first published month, until most of
 * the team joined or it is hidden at this workplace; nothing otherwise. [onInvite] opens the team,
 * where each person's invitation (e-mail or join code and link) is sent.
 */
@Composable
fun TeamInviteSection(viewModel: TeamInviteViewModel, onInvite: () -> Unit) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    // SH-247: decided after the dashboard: the card unfolds instead of popping in (and folds away).
    val join = com.evoapps.evoshift.ui.theme.rememberLastNonNull(state.join)
    com.evoapps.evoshift.ui.theme.MotionVisibility(state.join != null && join != null) {
        join?.let { TeamInvitePromptCard(it, onInvite = onInvite, onDismiss = viewModel::dismiss) }
    }
}


@Composable
fun TeamInvitePromptCard(join: TeamJoin, onInvite: () -> Unit, onDismiss: () -> Unit, modifier: Modifier = Modifier) {
    PromptCard(
        icon = Icons.Default.GroupAdd,
        title = stringResource(Res.string.team_invite_prompt_title),
        text = stringResource(Res.string.team_invite_prompt_text, join.joined, join.total),
        action = stringResource(Res.string.team_invite_prompt_action),
        dismiss = stringResource(Res.string.team_invite_prompt_dismiss),
        onAction = onInvite,
        onDismiss = onDismiss,
        modifier = modifier.testTag(GrowthCardTags.TEAM_INVITE)
    )
}

@Composable
private fun PromptCard(
    icon: ImageVector,
    title: String,
    text: String,
    action: String,
    dismiss: String,
    onAction: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier
) {
    RegularCard(modifier = modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(icon, contentDescription = null, tint = DeepNavy, modifier = Modifier.size(22.dp))
                Spacer(Modifier.width(10.dp))
                Text(title, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy, modifier = Modifier.weight(1f))
            }
            Text(text, style = MaterialTheme.typography.bodyMedium, color = DeepNavy)
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                GoldButton(text = action, onClick = onAction)
                TextButton(onClick = onDismiss, modifier = Modifier.handCursor()) {
                    Text(dismiss, color = DeepNavy, fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}
